import React, { useState } from "react";
import {
  Table,
  Card,
  Input,
  Select,
  Button,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Dropdown,
  Modal,
  Form,
  Tooltip,
  Divider,
  Alert,
  Upload,
  message,
  DatePicker,
  Space,
  Descriptions,
} from "antd";
import {
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  ShoppingCartOutlined,
  GiftOutlined,
  UploadOutlined,
  DownloadOutlined,
  TagOutlined,
  CreditCardOutlined,
  DownOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useOrders, useOrderStats, useOrderMutations } from "../hooks/useOrders";
import {
  ORDER_STATUS_OPTIONS,
  ORDER_SOURCE_OPTIONS,
  getStatusColor,
  getSourceColor,
  formatCurrency,
  formatDateTime,
} from "../services/orderService";
import config from "../../../config";

const { Title, Text } = Typography;
const { Search } = Input;

const OrdersListView = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sourceFilter, setSourceFilter] = useState("ALL");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [trackingForm] = Form.useForm();

  // New state for advanced features
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState({
    orderNumber: true,
    orderDate: true,
    customer: true,
    items: true,
    subtotal: true,
    discount: true,
    total: true,
    status: true,
    source: true,
    payment: true,
    tracking: true,
  });
  const [dateRange, setDateRange] = useState(null);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);

  const { data, isLoading, refetch } = useOrders({
    page,
    pageSize,
    searchTerm,
    statusFilter,
    sourceFilter,
  });

  const { data: stats } = useOrderStats();
  const { updateOrderStatus, addTracking, deleteOrder } = useOrderMutations();

  const orders = data?.orders || [];
  const totalElements = data?.totalElements || 0;

  const handleStatusChange = (orderId, newStatus) => {
    updateOrderStatus.mutate({ id: orderId, status: newStatus });
  };

  const handleAddTracking = (values) => {
    if (selectedOrder) {
      addTracking.mutate({
        id: selectedOrder.id,
        trackingNumber: values.trackingNumber,
        carrier: values.carrier,
      });
      setTrackingModalOpen(false);
      trackingForm.resetFields();
    }
  };

  const handleDelete = (orderId) => {
    Modal.confirm({
      title: "Delete Order",
      content: "Are you sure you want to delete this order? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: () => deleteOrder.mutate(orderId),
    });
  };

  const handleImportCsv = async (file) => {
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${config.SPRING_API_BASE}/api/admin/orders/import`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Import failed");
      }

      const result = await response.json();
      message.success(
        `Import completed! Imported: ${result.imported}, Skipped: ${result.skipped}, Failed: ${result.failed}`
      );
      refetch(); // Refresh the orders list
    } catch (error) {
      message.error(`Failed to import orders: ${error.message}`);
    } finally {
      setImporting(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleExportCsv = (selectedOnly = false) => {
    const exportOrders = selectedOnly
      ? orders.filter((order) => selectedRowKeys.includes(order.id))
      : orders;

    if (exportOrders.length === 0) {
      message.warning("No orders to export");
      return;
    }

    const csvHeaders = [
      "Order Number",
      "Date",
      "Customer",
      "Email",
      "Phone",
      "Items",
      "Subtotal",
      "Shipping",
      "Tax",
      "Discount",
      "Total",
      "Status",
      "Source",
      "Tracking",
      "Carrier",
      "Stripe Payment ID",
      "Promo Code",
      "Gift Order",
    ].join(",");

    const csvRows = exportOrders.map((order) => {
      return [
        order.orderNumber || "",
        formatDateTime(order.orderDate),
        `"${(order.shippingName || "").replace(/"/g, '""')}"`,
        order.shippingEmail || "",
        order.shippingPhone || "",
        order.items?.length || 0,
        order.subtotal || 0,
        order.shippingCost || 0,
        order.taxAmount || 0,
        order.discountAmount || 0,
        order.totalAmount || 0,
        order.status || "",
        order.source || "",
        order.trackingNumber || "",
        order.shippingCarrier || "",
        order.stripePaymentIntentId || "",
        `"${(order.discountCode || "").replace(/"/g, '""')}"`,
        order.isGift ? "Yes" : "No",
      ].join(",");
    });

    const csvContent = [csvHeaders, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `orders_export_${selectedOnly ? "selected_" : ""}${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success(`Exported ${exportOrders.length} order(s) successfully!`);
  };

  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select orders to delete");
      return;
    }

    Modal.confirm({
      title: `Delete ${selectedRowKeys.length} Order(s)`,
      content: `Are you sure you want to delete ${selectedRowKeys.length} selected order(s)? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await Promise.all(selectedRowKeys.map((id) => deleteOrder.mutateAsync(id)));
          setSelectedRowKeys([]);
          message.success(`Deleted ${selectedRowKeys.length} order(s) successfully`);
        } catch (error) {
          message.error("Failed to delete some orders");
        }
      },
    });
  };

  const handleBulkStatusChange = (newStatus) => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select orders to update");
      return;
    }

    Modal.confirm({
      title: `Update ${selectedRowKeys.length} Order(s)`,
      content: `Change status to ${newStatus} for ${selectedRowKeys.length} selected order(s)?`,
      okText: "Update",
      onOk: async () => {
        try {
          await Promise.all(
            selectedRowKeys.map((id) => updateOrderStatus.mutateAsync({ id, status: newStatus }))
          );
          setSelectedRowKeys([]);
          message.success(`Updated ${selectedRowKeys.length} order(s) successfully`);
        } catch (error) {
          message.error("Failed to update some orders");
        }
      },
    });
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
      {
        key: "paid",
        text: "Select Paid Orders",
        onSelect: () => {
          const paidKeys = orders
            .filter((order) => order.status === "PAID")
            .map((order) => order.id);
          setSelectedRowKeys(paidKeys);
        },
      },
      {
        key: "needsFulfillment",
        text: "Select Orders Needing Fulfillment",
        onSelect: () => {
          const needsFulfillmentKeys = orders
            .filter((order) => ["PAID", "PROCESSING"].includes(order.status))
            .map((order) => order.id);
          setSelectedRowKeys(needsFulfillmentKeys);
        },
      },
    ],
  };

  const getActionMenu = (record) => ({
    items: [
      {
        key: "view",
        icon: <EyeOutlined />,
        label: "View Details",
        onClick: () => {
          setSelectedOrder(record);
          setDetailModalOpen(true);
        },
      },
      {
        key: "tracking",
        icon: <TruckOutlined />,
        label: "Add Tracking",
        onClick: () => {
          setSelectedOrder(record);
          setTrackingModalOpen(true);
        },
      },
      {
        type: "divider",
      },
      {
        key: "processing",
        icon: <EditOutlined />,
        label: "Mark Processing",
        onClick: () => handleStatusChange(record.id, "PROCESSING"),
        disabled: record.status !== "PAID",
      },
      {
        key: "shipped",
        icon: <TruckOutlined />,
        label: "Mark Shipped",
        onClick: () => handleStatusChange(record.id, "SHIPPED"),
        disabled: !["PAID", "PROCESSING"].includes(record.status),
      },
      {
        key: "delivered",
        icon: <CheckCircleOutlined />,
        label: "Mark Delivered",
        onClick: () => handleStatusChange(record.id, "DELIVERED"),
        disabled: record.status !== "SHIPPED",
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        icon: <DeleteOutlined />,
        label: "Delete",
        danger: true,
        onClick: () => handleDelete(record.id),
        disabled: !["PENDING", "CANCELLED"].includes(record.status),
      },
    ],
  });

  const columns = [
    {
      title: "Order #",
      dataIndex: "orderNumber",
      key: "orderNumber",
      width: 140,
      fixed: "left",
      sorter: (a, b) => a.orderNumber.localeCompare(b.orderNumber),
      render: (text, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => {
              setSelectedOrder(record);
              setDetailModalOpen(true);
            }}
            style={{ padding: 0 }}
          >
            {text}
          </Button>
          {record.isGift && (
            <Tooltip title="Gift Order">
              <GiftOutlined style={{ color: "#fa8c16" }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 160,
      sorter: (a, b) => new Date(a.orderDate) - new Date(b.orderDate),
      defaultSortOrder: "descend",
      render: (date) => formatDateTime(date),
    },
    {
      title: "Customer",
      dataIndex: "shippingName",
      key: "customer",
      width: 200,
      ellipsis: true,
      sorter: (a, b) => (a.shippingName || "").localeCompare(b.shippingName || ""),
      render: (name, record) => (
        <div>
          <div>{name || "-"}</div>
          {record.shippingEmail && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.shippingEmail}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Items",
      dataIndex: "items",
      key: "items",
      width: 80,
      align: "center",
      render: (items) => items?.length || 0,
    },
    {
      title: "Subtotal",
      dataIndex: "subtotal",
      key: "subtotal",
      width: 100,
      align: "right",
      sorter: (a, b) => (a.subtotal || 0) - (b.subtotal || 0),
      render: (amount) => formatCurrency(amount),
    },
    {
      title: "Discount",
      dataIndex: "discountAmount",
      key: "discount",
      width: 120,
      align: "right",
      sorter: (a, b) => (a.discountAmount || 0) - (b.discountAmount || 0),
      render: (amount, record) =>
        amount > 0 ? (
          <Tooltip title={record.discountCode || "Discount applied"}>
            <Space direction="vertical" size={0}>
              <Text style={{ color: "#52c41a" }}>-{formatCurrency(amount)}</Text>
              {record.discountCode && (
                <Tag icon={<TagOutlined />} color="success" style={{ fontSize: 10 }}>
                  {record.discountCode}
                </Tag>
              )}
            </Space>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      key: "total",
      width: 100,
      align: "right",
      sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0),
      render: (amount) => <Text strong>{formatCurrency(amount)}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      filters: [
        { text: "Pending", value: "PENDING" },
        { text: "Paid", value: "PAID" },
        { text: "Processing", value: "PROCESSING" },
        { text: "Shipped", value: "SHIPPED" },
        { text: "Delivered", value: "DELIVERED" },
        { text: "Cancelled", value: "CANCELLED" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "PENDING",
                label: "Pending",
                onClick: () => handleStatusChange(record.id, "PENDING"),
              },
              {
                key: "PAID",
                label: "Paid",
                onClick: () => handleStatusChange(record.id, "PAID"),
              },
              {
                key: "PROCESSING",
                label: "Processing",
                onClick: () => handleStatusChange(record.id, "PROCESSING"),
              },
              {
                key: "SHIPPED",
                label: "Shipped",
                onClick: () => handleStatusChange(record.id, "SHIPPED"),
              },
              {
                key: "DELIVERED",
                label: "Delivered",
                onClick: () => handleStatusChange(record.id, "DELIVERED"),
              },
              {
                type: "divider",
              },
              {
                key: "CANCELLED",
                label: "Cancelled",
                danger: true,
                onClick: () => handleStatusChange(record.id, "CANCELLED"),
              },
            ],
          }}
          trigger={["click"]}
        >
          <Tag
            color={getStatusColor(status)}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            {status} <DownOutlined style={{ fontSize: 10 }} />
          </Tag>
        </Dropdown>
      ),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 100,
      filters: [
        { text: "Website", value: "WEBSITE" },
        { text: "In-Store", value: "IN_STORE" },
        { text: "Phone", value: "PHONE" },
      ],
      onFilter: (value, record) => record.source === value,
      render: (source) => <Tag color={getSourceColor(source)}>{source}</Tag>,
    },
    {
      title: "Payment",
      dataIndex: "stripePaymentIntentId",
      key: "payment",
      width: 130,
      render: (paymentId, record) =>
        paymentId ? (
          <Tooltip title={`Stripe Payment: ${paymentId}`}>
            <Space direction="vertical" size={0}>
              <Tag icon={<CreditCardOutlined />} color="blue" style={{ fontSize: 10 }}>
                {paymentId.substring(paymentId.length - 8)}
              </Tag>
              {record.paidAt && (
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {formatDateTime(record.paidAt)}
                </Text>
              )}
            </Space>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Tracking",
      dataIndex: "trackingNumber",
      key: "tracking",
      width: 150,
      render: (tracking, record) =>
        tracking ? (
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            <Text copyable={{ text: tracking }} style={{ fontSize: 12 }}>
              {tracking}
            </Text>
            {record.shippingCarrier && (
              <Tag color="purple" style={{ fontSize: 10 }}>
                {record.shippingCarrier}
              </Tag>
            )}
          </Space>
        ) : (
          <Button
            type="link"
            size="small"
            icon={<TruckOutlined />}
            onClick={() => {
              setSelectedOrder(record);
              setTrackingModalOpen(true);
            }}
            style={{ padding: 0 }}
          >
            Add Tracking
          </Button>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getActionMenu(record)} trigger={["click"]}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
          <ShoppingCartOutlined style={{ marginRight: 12 }} />
          Online Orders
        </Title>
        <Text type="secondary">Manage orders from the website</Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Total Orders"
              value={stats?.totalOrders || 0}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Needs Fulfillment"
              value={stats?.needsFulfillment || 0}
              valueStyle={{ color: "#fa8c16" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Shipped"
              value={stats?.shippedOrders || 0}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Revenue"
              value={stats?.totalRevenue || 0}
              prefix="$"
              precision={2}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Bulk Actions Toolbar */}
      {selectedRowKeys.length > 0 && (
        <Card size="small" style={{ marginBottom: 16, backgroundColor: "#e6f7ff", borderColor: "#91d5ff" }}>
          <Row gutter={[16, 16]} align="middle">
            <Col flex="auto">
              <Text strong style={{ fontSize: 16 }}>
                {selectedRowKeys.length} order(s) selected
              </Text>
            </Col>
            <Col>
              <Space>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "PROCESSING",
                        label: "Mark as Processing",
                        onClick: () => handleBulkStatusChange("PROCESSING"),
                      },
                      {
                        key: "SHIPPED",
                        label: "Mark as Shipped",
                        onClick: () => handleBulkStatusChange("SHIPPED"),
                      },
                      {
                        key: "DELIVERED",
                        label: "Mark as Delivered",
                        onClick: () => handleBulkStatusChange("DELIVERED"),
                      },
                      {
                        key: "CANCELLED",
                        label: "Mark as Cancelled",
                        onClick: () => handleBulkStatusChange("CANCELLED"),
                        danger: true,
                      },
                    ],
                  }}
                >
                  <Button>
                    Bulk Status Change <DownOutlined />
                  </Button>
                </Dropdown>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => handleExportCsv(true)}
                >
                  Export Selected
                </Button>
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={handleBulkDelete}
                >
                  Delete Selected
                </Button>
                <Button onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Search
              placeholder="Search orders..."
              allowClear
              onSearch={(value) => {
                setSearchTerm(value);
                setPage(0);
              }}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setPage(0);
              }}
              style={{ width: "100%" }}
              options={ORDER_STATUS_OPTIONS}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              value={sourceFilter}
              onChange={(value) => {
                setSourceFilter(value);
                setPage(0);
              }}
              style={{ width: "100%" }}
              options={ORDER_SOURCE_OPTIONS}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              style={{ width: "100%" }}
              placeholder={["Start Date", "End Date"]}
            />
          </Col>
          <Col xs={12} sm={4} md={2}>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
          </Col>
        </Row>
        <Divider style={{ margin: "16px 0" }} />
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Space>
              <Upload
                accept=".csv"
                beforeUpload={handleImportCsv}
                showUploadList={false}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={importing}
                  type="primary"
                >
                  Import CSV
                </Button>
              </Upload>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleExportCsv(false)}
                disabled={orders.length === 0}
              >
                Export All
              </Button>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setColumnSettingsOpen(true)}
              >
                Columns
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Orders Table */}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Text strong>
              Showing {orders.length} order(s) on this page
            </Text>
            <Text type="secondary">|</Text>
            <Text strong style={{ color: "#52c41a" }}>
              Total: {formatCurrency(orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0))}
            </Text>
            {(searchTerm || statusFilter !== "ALL" || sourceFilter !== "ALL" || dateRange) && (
              <>
                <Text type="secondary">|</Text>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("ALL");
                    setSourceFilter("ALL");
                    setDateRange(null);
                    setPage(0);
                    refetch();
                  }}
                >
                  Clear All Filters
                </Button>
              </>
            )}
          </Space>
        </div>
        <Table
          rowSelection={rowSelection}
          columns={columns.filter((col) => {
            if (!col.dataIndex && !col.key) return true; // Always show actions column
            const key = col.dataIndex || col.key;
            return visibleColumns[key] !== false;
          })}
          dataSource={orders}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page + 1,
            pageSize,
            total: totalElements,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} orders`,
            onChange: (newPage, newPageSize) => {
              setPage(newPage - 1);
              setPageSize(newPageSize);
            },
          }}
          scroll={{ x: 1600 }}
          size="small"
        />
      </Card>

      {/* Column Settings Modal */}
      <Modal
        title="Customize Columns"
        open={columnSettingsOpen}
        onCancel={() => setColumnSettingsOpen(false)}
        onOk={() => setColumnSettingsOpen(false)}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">Select which columns to display in the table:</Text>
        </div>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>
            <input
              type="checkbox"
              checked={visibleColumns.orderNumber}
              onChange={(e) =>
                setVisibleColumns({ ...visibleColumns, orderNumber: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <Text>Order Number</Text>
          </div>
          <div>
            <input
              type="checkbox"
              checked={visibleColumns.orderDate}
              onChange={(e) =>
                setVisibleColumns({ ...visibleColumns, orderDate: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <Text>Order Date</Text>
          </div>
          <div>
            <input
              type="checkbox"
              checked={visibleColumns.customer}
              onChange={(e) =>
                setVisibleColumns({ ...visibleColumns, customer: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <Text>Customer</Text>
          </div>
          <div>
            <input
              type="checkbox"
              checked={visibleColumns.items}
              onChange={(e) =>
                setVisibleColumns({ ...visibleColumns, items: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <Text>Items Count</Text>
          </div>
          <div>
            <input
              type="checkbox"
              checked={visibleColumns.subtotal}
              onChange={(e) =>
                setVisibleColumns({ ...visibleColumns, subtotal: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <Text>Subtotal</Text>
          </div>
          <div>
            <input
              type="checkbox"
              checked={visibleColumns.discount}
              onChange={(e) =>
                setVisibleColumns({ ...visibleColumns, discount: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <Text>Discount</Text>
          </div>
          <div>
            <input
              type="checkbox"
              checked={visibleColumns.total}
              onChange={(e) =>
                setVisibleColumns({ ...visibleColumns, total: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <Text>Total</Text>
          </div>
          <div>
            <input
              type="checkbox"
              checked={visibleColumns.status}
              onChange={(e) =>
                setVisibleColumns({ ...visibleColumns, status: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <Text>Status</Text>
          </div>
          <div>
            <input
              type="checkbox"
              checked={visibleColumns.source}
              onChange={(e) =>
                setVisibleColumns({ ...visibleColumns, source: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <Text>Source</Text>
          </div>
          <div>
            <input
              type="checkbox"
              checked={visibleColumns.payment}
              onChange={(e) =>
                setVisibleColumns({ ...visibleColumns, payment: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <Text>Payment Info</Text>
          </div>
          <div>
            <input
              type="checkbox"
              checked={visibleColumns.tracking}
              onChange={(e) =>
                setVisibleColumns({ ...visibleColumns, tracking: e.target.checked })
              }
              style={{ marginRight: 8 }}
            />
            <Text>Tracking</Text>
          </div>
        </Space>
        <Divider />
        <Button
          block
          onClick={() =>
            setVisibleColumns({
              orderNumber: true,
              orderDate: true,
              customer: true,
              items: true,
              subtotal: true,
              discount: true,
              total: true,
              status: true,
              source: true,
              payment: true,
              tracking: true,
            })
          }
        >
          Reset to Default
        </Button>
      </Modal>

      {/* Order Detail Modal */}
      <Modal
        title={`Order ${selectedOrder?.orderNumber || ""}`}
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        footer={null}
        width={800}
      >
        {selectedOrder && (
          <div>
            {/* Gift Order Alert */}
            {selectedOrder.isGift && (
              <Alert
                type="info"
                icon={<GiftOutlined />}
                showIcon
                style={{ marginBottom: 16 }}
                message="Gift Order"
                description={selectedOrder.giftMessage || "No gift message provided"}
              />
            )}

            {/* Promo Code Alert */}
            {selectedOrder.discountAmount > 0 && selectedOrder.discountCode && (
              <Alert
                type="success"
                icon={<TagOutlined />}
                showIcon
                style={{ marginBottom: 16 }}
                message={`Promo Code Applied: ${selectedOrder.discountCode}`}
                description={`Discount: ${formatCurrency(selectedOrder.discountAmount)}`}
              />
            )}

            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(selectedOrder.status)}>
                  {selectedOrder.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Source">
                <Tag color={getSourceColor(selectedOrder.source)}>
                  {selectedOrder.source}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Order Date">
                {formatDateTime(selectedOrder.orderDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                <Text strong style={{ fontSize: 16 }}>
                  {formatCurrency(selectedOrder.totalAmount)}
                </Text>
              </Descriptions.Item>
              {selectedOrder.stripePaymentIntentId && (
                <Descriptions.Item label="Stripe Payment ID" span={2}>
                  <Space>
                    <Tag icon={<CreditCardOutlined />} color="blue">
                      {selectedOrder.stripePaymentIntentId}
                    </Tag>
                    <Text copyable={{ text: selectedOrder.stripePaymentIntentId }} />
                  </Space>
                </Descriptions.Item>
              )}
              {selectedOrder.stripeCustomerId && (
                <Descriptions.Item label="Stripe Customer ID" span={2}>
                  <Space>
                    <Tag color="blue">{selectedOrder.stripeCustomerId}</Tag>
                    <Text copyable={{ text: selectedOrder.stripeCustomerId }} />
                  </Space>
                </Descriptions.Item>
              )}
              {selectedOrder.paidAt && (
                <Descriptions.Item label="Payment Date" span={2}>
                  {formatDateTime(selectedOrder.paidAt)}
                </Descriptions.Item>
              )}
              {selectedOrder.shippedAt && (
                <Descriptions.Item label="Shipped Date" span={2}>
                  {formatDateTime(selectedOrder.shippedAt)}
                </Descriptions.Item>
              )}
              {selectedOrder.deliveredAt && (
                <Descriptions.Item label="Delivered Date" span={2}>
                  {formatDateTime(selectedOrder.deliveredAt)}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            {/* Order Items */}
            <div>
              <Title level={5}>Order Items</Title>
              {selectedOrder.items && selectedOrder.items.length > 0 ? (
                <Table
                  dataSource={selectedOrder.items}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: "Product",
                      dataIndex: "productName",
                      key: "productName",
                      render: (name) => name || "Unknown Product",
                    },
                    {
                      title: "Qty",
                      dataIndex: "quantity",
                      key: "quantity",
                      width: 60,
                      align: "center",
                    },
                    {
                      title: "Price",
                      dataIndex: "unitPrice",
                      key: "unitPrice",
                      width: 80,
                      align: "right",
                      render: (price) => formatCurrency(price),
                    },
                    {
                      title: "Total",
                      dataIndex: "lineTotal",
                      key: "lineTotal",
                      width: 80,
                      align: "right",
                      render: (total) => formatCurrency(total),
                    },
                  ]}
                />
              ) : selectedOrder.customerNotes ? (
                <div style={{ background: "#fafafa", padding: 12, borderRadius: 6 }}>
                  <Text>{selectedOrder.customerNotes}</Text>
                </div>
              ) : (
                <Text type="secondary">No items recorded</Text>
              )}
            </div>

            <Divider />

            {/* Shipping Address */}
            <div>
              <Title level={5}>Shipping Address</Title>
              {selectedOrder.shippingName && <div><strong>{selectedOrder.shippingName}</strong></div>}
              {selectedOrder.shippingAddressLine1 && <div>{selectedOrder.shippingAddressLine1}</div>}
              {selectedOrder.shippingAddressLine2 && <div>{selectedOrder.shippingAddressLine2}</div>}
              {(selectedOrder.shippingCity || selectedOrder.shippingState || selectedOrder.shippingZip) && (
                <div>
                  {[selectedOrder.shippingCity, selectedOrder.shippingState, selectedOrder.shippingZip]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              )}
              {selectedOrder.shippingEmail && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Email:</Text> {selectedOrder.shippingEmail}
                </div>
              )}
              {selectedOrder.shippingPhone && (
                <div>
                  <Text type="secondary">Phone:</Text> {selectedOrder.shippingPhone}
                </div>
              )}
            </div>

            {/* Tracking */}
            {selectedOrder.trackingNumber && (
              <>
                <Divider />
                <div>
                  <Title level={5}>Tracking</Title>
                  <div>
                    <Tag color="purple">{selectedOrder.shippingCarrier || "Carrier"}</Tag>
                    <Text copyable={{ text: selectedOrder.trackingNumber }}>
                      {selectedOrder.trackingNumber}
                    </Text>
                  </div>
                </div>
              </>
            )}

            <Divider />

            {/* Order Summary */}
            <div>
              <Title level={5}>Order Summary</Title>
              <Row>
                <Col span={16}>Subtotal:</Col>
                <Col span={8} style={{ textAlign: "right" }}>
                  {formatCurrency(selectedOrder.subtotal)}
                </Col>
              </Row>
              <Row>
                <Col span={16}>Shipping:</Col>
                <Col span={8} style={{ textAlign: "right" }}>
                  {selectedOrder.shippingCost == 0 ? (
                    <Text type="success">FREE</Text>
                  ) : (
                    formatCurrency(selectedOrder.shippingCost)
                  )}
                </Col>
              </Row>
              {selectedOrder.taxAmount > 0 && (
                <Row>
                  <Col span={16}>Tax:</Col>
                  <Col span={8} style={{ textAlign: "right" }}>
                    {formatCurrency(selectedOrder.taxAmount)}
                  </Col>
                </Row>
              )}
              {selectedOrder.discountAmount > 0 && (
                <Row>
                  <Col span={16}>
                    <Space>
                      <span>Discount:</span>
                      {selectedOrder.discountCode && (
                        <Tag icon={<TagOutlined />} color="success" style={{ fontSize: 10 }}>
                          {selectedOrder.discountCode}
                        </Tag>
                      )}
                    </Space>
                  </Col>
                  <Col span={8} style={{ textAlign: "right", color: "#52c41a" }}>
                    -{formatCurrency(selectedOrder.discountAmount)}
                  </Col>
                </Row>
              )}
              <Divider style={{ margin: "12px 0" }} />
              <Row style={{ fontWeight: 600, fontSize: 16 }}>
                <Col span={16}>Total:</Col>
                <Col span={8} style={{ textAlign: "right" }}>
                  {formatCurrency(selectedOrder.totalAmount)}
                </Col>
              </Row>
            </div>

            {/* Notes */}
            {selectedOrder.notes && (
              <>
                <Divider />
                <div>
                  <Title level={5}>Internal Notes</Title>
                  <div>{selectedOrder.notes}</div>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Add Tracking Modal */}
      <Modal
        title="Add Tracking Information"
        open={trackingModalOpen}
        onCancel={() => {
          setTrackingModalOpen(false);
          trackingForm.resetFields();
        }}
        onOk={() => trackingForm.submit()}
        okText="Save Tracking"
      >
        <Form form={trackingForm} layout="vertical" onFinish={handleAddTracking}>
          <Form.Item
            name="trackingNumber"
            label="Tracking Number"
            rules={[{ required: true, message: "Please enter tracking number" }]}
          >
            <Input placeholder="Enter tracking number" />
          </Form.Item>
          <Form.Item name="carrier" label="Carrier">
            <Select placeholder="Select carrier">
              <Select.Option value="USPS">USPS</Select.Option>
              <Select.Option value="UPS">UPS</Select.Option>
              <Select.Option value="FedEx">FedEx</Select.Option>
              <Select.Option value="DHL">DHL</Select.Option>
              <Select.Option value="Other">Other</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OrdersListView;
